import React, { useState, useCallback, useRef, useEffect } from "react";
import useFetch from "../../../../../hooks/useFetch";

// Phase 2 additions to the Phase 1 hook: pauseEvent, resumeEvent,
// finalizeResults. Everything else is unchanged — diff against your
// Phase 1 copy if you've since customized it further.

const LIVE_FEED_POLL_MS = 15000;

export default function useReunionAdmin() {
  const { fetchData } = useFetch();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [liveFeed, setLiveFeed] = useState(null);
  const [winners, setWinners] = useState(null);

  const pollRef = useRef(null);

  const loadMyEvent = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await fetchData({ url: "/reunion/my-halls", method: "POST" });
      if (response?.status) {
        setEvent(response.data?.[0] || null);
      } else {
        setError(response?.message || "Could not load your Reunion.");
      }
    } catch (err) {
      setError("Unable to reach the server.");
    } finally {
      setLoading(false);
    }
  }, [fetchData]);

  const createEvent = useCallback(
    async (payload) => {
      try {
        setLoading(true);
        setError("");
        const response = await fetchData({ url: "/reunion/create", method: "POST", data: payload });
        if (response?.status) {
          setEvent(response.data);
          return response.data;
        }
        setError(response?.message || "Could not create Reunion.");
        return null;
      } catch (err) {
        setError("Unable to reach the server.");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [fetchData]
  );

  const updateEvent = useCallback(
    async (partialPayload) => {
      if (!event?._id) return null;
      try {
        setLoading(true);
        setError("");
        const response = await fetchData({
          url: `/reunion/update/${event._id}`,
          method: "PUT",
          data: partialPayload,
        });
        if (response?.status) {
          setEvent(response.data);
          return response.data;
        }
        setError(response?.message || "Could not update Reunion.");
        return null;
      } catch (err) {
        setError("Unable to reach the server.");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [event, fetchData]
  );

  const startEvent = useCallback(async () => {
    if (!event?._id) return null;
    try {
      setError("");
      const response = await fetchData({ url: `/reunion/start/${event._id}`, method: "PUT" });
      if (response?.status) {
        setEvent(response.data);
        return response.data;
      }
      setError(response?.message || "Could not start Reunion.");
      return null;
    } catch (err) {
      setError("Unable to reach the server.");
      return null;
    }
  }, [event, fetchData]);

  const stopEvent = useCallback(async () => {
    if (!event?._id) return null;
    try {
      setError("");
      const response = await fetchData({ url: `/reunion/stop/${event._id}`, method: "PUT" });
      if (response?.status) {
        setEvent(response.data);
        return response.data;
      }
      setError(response?.message || "Could not stop Reunion.");
      return null;
    } catch (err) {
      setError("Unable to reach the server.");
      return null;
    }
  }, [event, fetchData]);

  // --- Phase 2: pause / resume ---
  const pauseEvent = useCallback(async () => {
    if (!event?._id) return null;
    try {
      setError("");
      const response = await fetchData({
        url: `/reunion/game/pause/${event._id}`,
        method: "PUT",
      });
      if (response?.status) {
        setEvent(response.data);
        return response.data;
      }
      setError(response?.message || "Could not pause.");
      return null;
    } catch (err) {
      setError("Unable to reach the server.");
      return null;
    }
  }, [event, fetchData]);

  const resumeEvent = useCallback(async () => {
    if (!event?._id) return null;
    try {
      setError("");
      const response = await fetchData({
        url: `/reunion/game/resume/${event._id}`,
        method: "PUT",
      });
      if (response?.status) {
        setEvent(response.data);
        return response.data;
      }
      setError(response?.message || "Could not resume.");
      return null;
    } catch (err) {
      setError("Unable to reach the server.");
      return null;
    }
  }, [event, fetchData]);

  const finalizeResults = useCallback(async () => {
    if (!event?._id) return null;
    try {
      setError("");
      const response = await fetchData({
        url: `/reunion/game/finalize/${event._id}`,
        method: "POST",
      });
      if (!response?.status) {
        setError(response?.message || "Could not finalize results.");
        return null;
      }
      return response.data;
    } catch (err) {
      setError("Unable to reach the server.");
      return null;
    }
  }, [event, fetchData]);

  const fetchLiveFeedOnce = useCallback(async () => {
    if (!event?._id) return;
    try {
      const response = await fetchData({ url: `/reunion/live/${event._id}`, method: "POST" });
      if (response?.status) setLiveFeed(response.data);
    } catch (err) {
      // silent — see Phase 1 rationale
    }
  }, [event, fetchData]);

  const startLiveFeedPolling = useCallback(() => {
    fetchLiveFeedOnce();
    pollRef.current = setInterval(fetchLiveFeedOnce, LIVE_FEED_POLL_MS);
  }, [fetchLiveFeedOnce]);

  const stopLiveFeedPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopLiveFeedPolling();
    // eslint-disable-next-line
  }, []);

  const fetchWinners = useCallback(async () => {
    if (!event?._id) return null;
    try {
      setError("");
      const response = await fetchData({ url: `/reunion/winners/${event._id}`, method: "POST" });
      if (response?.status) {
        setWinners(response.data);
        return response.data;
      }
      setError(response?.message || "Could not load winners.");
      return null;
    } catch (err) {
      setError("Unable to reach the server.");
      return null;
    }
  }, [event, fetchData]);

  return {
    event,
    loading,
    error,
    loadMyEvent,
    createEvent,
    updateEvent,
    startEvent,
    stopEvent,
    pauseEvent,
    resumeEvent,
    finalizeResults,
    liveFeed,
    startLiveFeedPolling,
    stopLiveFeedPolling,
    winners,
    fetchWinners,
  };
}
